import React, {useState, useEffect} from 'react';
import {Window, Button, ProgressBar, Fieldset} from 'react95';
import styles from '../styles/modules/Window.module.css';

interface WindowEasterEggsProps {
  onClose: () => void;
}

const WindowEasterEggs: React.FC<WindowEasterEggsProps> = ({onClose}) => {
  const [stage, setStage] = useState<'menu' | 'scanning' | 'virus' | 'bsod'>('menu');
  const [scanProgress, setScanProgress] = useState(0);
  const [virusFound, setVirusFound] = useState(false);
  const [scanningFiles, setScanningFiles] = useState('');
  const [virusCount, setVirusCount] = useState(0);

  const fakeFileNames = [
    'C:\\WINDOWS\\SYSTEM32\\KERNEL32.DLL',
    'C:\\WINDOWS\\SYSTEM32\\USER32.DLL',
    'C:\\WINDOWS\\SYSTEM\\LOVE.EXE',
    'C:\\WINDOWS\\SYSTEM\\HAPPINESS.DLL',
    'C:\\PROGRAM FILES\\WEDDING\\CELEBRATION.EXE',
    'C:\\TEMP\\ROMANCE.TMP',
    'C:\\WINDOWS\\SYSTEM32\\MARRIAGE.SYS',
    'C:\\DOCUMENTS\\WEDDING PLANS\\GUEST_LIST.DOC',
    'C:\\PROGRAM FILES\\PARTY\\MUSIC.WAV',
    'C:\\WINDOWS\\SYSTEM\\JOY.DLL',
  ];

  const virusNames = [
    'Win32.LoveVirus',
    'Trojan.WeddingCelebration',
    'Backdoor.Happiness',
    'Worm.Romance',
    'Malware.Party',
  ];

  useEffect(() => {
    if (stage === 'scanning') {
      const interval = setInterval(() => {
        setScanProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setTimeout(() => setStage('virus'), 1000);
            return 100;
          }
          
          // Update current file being scanned
          const fileIndex = Math.floor((prev / 100) * fakeFileNames.length);
          if (fileIndex < fakeFileNames.length) {
            setScanningFiles(fakeFileNames[fileIndex]);
          }
          
          const increment = Math.random() * 15 + 5;
          return Math.min(prev + increment, 100);
        });
      }, 300);

      return () => clearInterval(interval);
    }
  }, [stage]);

  useEffect(() => {
    if (stage === 'virus') {
      const interval = setInterval(() => {
        setVirusCount(prev => {
          if (prev >= 5) {
            clearInterval(interval);
            setTimeout(() => setVirusFound(true), 1000);
            return 5;
          }
          return prev + 1;
        });
      }, 800);

      return () => clearInterval(interval);
    }
  }, [stage]);

  const handleStartScan = () => {
    setStage('scanning');
    setScanProgress(0);
    setVirusCount(0);
    setVirusFound(false);
  };

  const handleSystemCrash = () => {
    setStage('bsod');
  };

  const renderMenu = () => (
    <div className={styles.easterEggsMenu}>
      <h2>🎉 Секретное меню</h2>
      <div className={styles.easterEggsOptions}>
        <Button onClick={handleStartScan} className={styles.easterEggButton}>
          🔍 Сканировать систему
        </Button>
        <Button onClick={() => setStage('bsod')} className={styles.easterEggButton}>
          💀 Вызвать BSOD
        </Button>
      </div>
    </div>
  );

  const renderScanning = () => (
    <div className={styles.scanningContainer}>
      <h2>🔍 Сканирование системы</h2>
      <Fieldset label="Процесс сканирования">
        <div className={styles.scanProgress}>
          <ProgressBar value={Math.floor(scanProgress)} />
          <div className={styles.scanStatus}>
            <p>Прогресс: {Math.floor(scanProgress)}%</p>
            <p className={styles.scanningFile}>Сканируется: {scanningFiles}</p>
          </div>
        </div>
      </Fieldset>
    </div>
  );

  const renderVirusScanner = () => (
    <div className={styles.virusContainer}>
      <h2>⚠️ Обнаружены угрозы!</h2>
      <Fieldset label="Результаты сканирования">
        <div className={styles.virusResults}>
          <p>Найдено вирусов: {virusCount}</p>
          <div className={styles.virusList}>
            {virusNames.slice(0, virusCount).map((virus, idx) => (
              <div key={idx} className={styles.virusItem}>
                <span className={styles.virusIcon}>🦠</span>
                <span>{virus}</span>
              </div>
            ))}
          </div>
          {virusFound && (
            <div className={styles.virusActions}>
              <p className={styles.virusWarning}>
                ⚠️ ВНИМАНИЕ! Обнаружены критические угрозы безопасности!
              </p>
              <Button onClick={handleSystemCrash} className={styles.crashButton}>
                🔧 Исправить проблемы
              </Button>
            </div>
          )}
        </div>
      </Fieldset>
    </div>
  );

  const renderBSOD = () => (
    <div className={`${styles.bsodContainer} ${styles.mobileBsod}`}>
      <div className={styles.bsodScreen}>
        <h1>💙 WINDOWS</h1>
        <p>Произошла фатальная ошибка системы и Windows была остановлена во избежание повреждения компьютера.</p>
        <p>Проблема вызвана следующей ошибкой:</p>
        <p className={styles.bsodError}>LOVE_OVERFLOW_EXCEPTION</p>
        <p>Если эта ошибка появилась впервые, перезагрузите компьютер. Если она появляется повторно, следуйте инструкциям:</p>
        <ul>
          <li>Проверьте наличие свободного места в сердце</li>
          <li>Убедитесь, что все романтические драйверы установлены корректно</li>
          <li>Запустите сканирование на предмет избытка счастья</li>
        </ul>
        <p>Техническая информация:</p>
        <p className={styles.bsodTech}>
          *** STOP: 0x000000LOVE (0xWEDDING, 0xHAPPINESS, 0xFOREVER, 0xTOGETHER)
        </p>
        <div className={styles.bsodActions}>
          <Button onClick={onClose} className={styles.bsodButton}>
            💔 Принудительная перезагрузка
          </Button>
          <Button onClick={() => setStage('menu')} className={styles.bsodButton}>
            🔙 Вернуться в меню
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Window className={`${styles.window} ${styles.easterEggsWindow}`}>
      <div className={styles.windowHeader}>
        <span>🎯 Системные утилиты</span>
        <Button onClick={onClose} size="sm">
          <span>✕</span>
        </Button>
      </div>

      <div className={styles.windowContent}>
        {stage === 'menu' && renderMenu()}
        {stage === 'scanning' && renderScanning()}
        {stage === 'virus' && renderVirusScanner()}
        {stage === 'bsod' && renderBSOD()}
      </div>
    </Window>
  );
};

export default WindowEasterEggs; 